import * as React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';

export type TimelineState = 'done' | 'current' | 'upcoming' | 'failed';

export interface TimelineEntry {
  key: string;
  label: string;
  /** When it happened, or what is expected — shown under the label. */
  detail?: React.ReactNode;
  /**
   * Where to go to unblock this step, when the seller can. A step held up by
   * something they own — no payout method attached, say — is more useful as
   * the way to fix it than as a description of the problem.
   */
  href?: string;
  state: TimelineState;
}

export interface TimelineProps {
  entries: TimelineEntry[];
  className?: string;
}

const dotFor: Record<TimelineState, string> = {
  done: 'bg-emerald-600',
  current: 'bg-amber-500',
  // Hollow, not a grey disc. A filled dot reads as an event that happened in
  // some neutral way; a ring reads as a slot waiting to be filled, which is
  // what a step that has not been reached yet is.
  upcoming: 'border-2 border-border bg-card',
  failed: 'bg-destructive',
};

/**
 * Where an order has got to, along its whole arc.
 *
 * A status badge says where something is now; this says where it has been and
 * what is still to come, which is what a seller is actually asking when they
 * open an order.
 *
 * Read downwards, on a rail, the way an order history reads in the Shopify
 * admin a seller already knows. It was four cells of a horizontal grid, which
 * had two problems: side by side, four equal boxes read as a set of statistics
 * rather than as one thing happening after another; and in the column this now
 * lives in there was never room for four across, so it wrapped to a two-by-two
 * block — the least sequential shape available.
 *
 * Entries with no recorded time are legitimate — orders placed before the
 * lifecycle timestamps existed have none — so a missing detail reads as
 * unknown rather than as not-yet-happened.
 */
export function Timeline({ entries, className }: TimelineProps) {
  // A list that stops on a failure has not finished, it has been cut short —
  // so the rail fades out under the last dot instead of ending on it. The
  // steps that would have followed are not drawn at all: they are not waiting
  // to happen, they are never going to.
  const stopped = entries[entries.length - 1]?.state === 'failed';
  return (
    <ol className={cn('min-w-0', className)}>
      {entries.map((entry, index) => (
        <li
          key={entry.key}
          data-testid={`timeline-${entry.key}`}
          data-state={entry.state}
          aria-current={entry.state === 'current' ? 'step' : undefined}
          className={cn(
            'relative flex gap-3 pb-4',
            // The last row keeps its space when the rail is fading through it.
            stopped ? 'last:pb-6' : 'last:pb-0',
          )}
        >
          {stopped && index === entries.length - 1 && (
            <span
              aria-hidden
              className="absolute left-1 top-[18px] h-6 w-0.5 bg-gradient-to-b from-border to-transparent"
            />
          )}
          {/* The rail, drawn from under this dot into the next one. Left is
              the dot's own centre: a 10px dot starting at 0 has its middle at
              5, and a 2px line at 4 covers 4–6. It overhangs the row by the
              dot's own top offset so the line meets the next dot instead of
              stopping short of it — segments with gaps read as four separate
              markers again, which is the thing this replaced. Neutral
              throughout: the dots already carry the state, and colouring the
              line as well would say it twice. */}
          {index < entries.length - 1 && (
            <span
              aria-hidden
              className="absolute -bottom-1.5 left-1 top-[18px] w-0.5 bg-border"
            />
          )}
          <span
            aria-hidden
            className={cn(
              'relative z-10 mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
              dotFor[entry.state],
            )}
          />
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'text-sm font-medium',
                entry.state === 'upcoming' ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {entry.label}
            </p>
            {entry.detail &&
              (entry.href ? (
                <Link
                  to={entry.href}
                  className="mt-0.5 block text-xs font-medium text-primary hover:underline"
                >
                  {entry.detail} →
                </Link>
              ) : (
                <p className="mt-0.5 text-xs text-muted-foreground">{entry.detail}</p>
              ))}
          </div>
          {/* The dot colour carries the state visually; this carries it to a
              screen reader, which cannot see the colour. */}
          <span className="sr-only">{stateLabel[entry.state]}</span>
        </li>
      ))}
    </ol>
  );
}

const stateLabel: Record<TimelineState, string> = {
  done: 'Completed',
  current: 'In progress',
  upcoming: 'Not started',
  failed: 'Failed',
};
